import type { ReactNode } from 'react';
import { PixelBeanSprite } from './dock-icons';

interface CoralHeaderProps {
  eyebrow: string;      // PIXEL LABEL row, e.g. "LV.4 · 340 / 500 XP"
  title: string;        // headline: level title, shop name, or state
  statusLabel: string;  // small centred label above the headline, e.g. shop name
  chip?: ReactNode;     // 46px right-side slot — initials chip / Save / LIVE
  xpPercent?: number;   // renders the white XP track when defined
  beanCount?: number;   // THE gold bean + count, above the chip (customer shell)
  variant?: 'coral' | 'grey'; // grey = offline (#10b)
}

// The mockup's "9:41 / ▮▮▮" row represented the REAL iPhone status bar — we
// don't fake one. The header instead paints under the device status bar /
// Dynamic Island (viewport-fit=cover + black-translucent) and pads content
// below it with safe-area-inset-top, so iOS renders its own clock over coral.
export default function CoralHeader({
  eyebrow, title, statusLabel, chip, xpPercent, beanCount, variant = 'coral',
}: CoralHeaderProps) {
  return (
    <header
      className={`relative overflow-hidden rounded-b-[24px] px-[22px] pb-[16px] pt-[calc(env(safe-area-inset-top)+10px)] ${
        variant === 'coral' ? 'bg-coral' : 'bg-body-grey'
      }`}
    >
      {/* Decorative disc sits with its centre ON the header's top edge, so
          its visible left flank runs all the way up — no coral sliver above
          it (Nik's on-device call 2026-08-06). */}
      <div
        className={`absolute -top-[70px] -right-[26px] w-[140px] h-[140px] pixel-circle ${
          variant === 'coral' ? 'bg-accent-alt' : 'bg-white/15'
        }`}
      />
      <div className="relative text-center">
        <span className="inline-block max-w-full truncate font-heading text-[8px] uppercase text-white/85 tracking-[1px]">
          {statusLabel}
        </span>
      </div>
      <div className="relative flex items-end justify-between gap-3.5 mt-3">
        <div className="min-w-0">
          <p className="font-heading text-[8px] text-white/85 tracking-[1px] uppercase mb-2.5">{eyebrow}</p>
          {/* DotGothic16 — "slightly pixelated", between grotesk and the
              full Press Start 2P labels (Nik's on-device call 2026-08-06). */}
          <p className="font-body font-bold text-3xl leading-[1.1] text-white truncate">{title}</p>
        </div>
        {/* Bean pill rides LEFT of the avatar chip (Nik's on-device call
            2026-08-06 — was stacked above it). */}
        <div className="flex items-center gap-2 shrink-0">
          {typeof beanCount === 'number' && (
            <span className="flex items-center gap-1.5 pixel-corners-sm bg-black/[.18] px-3 py-1.5">
              <PixelBeanSprite className="w-4 h-[17px]" />
              <span className="font-heading text-[9px] text-white leading-none">{beanCount}</span>
            </span>
          )}
          {chip}
        </div>
      </div>
      {typeof xpPercent === 'number' && (
        <div className="pixel-bar pixel-corners-sm [--bar-gap:#ffffff] relative h-3.5 bg-white mt-3 overflow-hidden">
          <div
            data-testid="xp-fill"
            style={{ width: `${xpPercent}%` }}
            className="h-full bg-pixel-cyan transition-[width] duration-300"
          />
        </div>
      )}
    </header>
  );
}
